import {Node} from "@xyflow/react";
import {
    HORIZONTAL_NODE_GAP
} from "./FamilyTreeLayoutConstants";
import {buildChildrenByParent, buildPartnerLeftOf} from "./FamilyTreePartnerLayout";
import {getNodeWidth} from "./FamilyTreeNodeSizing";
export function alignLeafChildPairsToParents(
    levelMap: Map<number, Node[]>,
    parentMap: Map<string, string[]>,
    partnerRightOf: Map<string, string>,
    nodes: Node[]
) {
    const partnerLeftOf = buildPartnerLeftOf(partnerRightOf);
    const childrenByParent = buildChildrenByParent(parentMap);
    const nodeById = new Map(nodes.map(node => [node.id, node]));
    const levelById = new Map<string, number>();
    levelMap.forEach((nodesAtLevel, level) => {
        nodesAtLevel.forEach(node => levelById.set(node.id, level));
    });

    const getPartnerId = (id: string) => partnerRightOf.get(id) ?? partnerLeftOf.get(id);
    const getPairCenter = (left: Node, right: Node) => {
        const leftEdge = (left.position.x ?? 0) - getNodeWidth(left) / 2;
        const rightEdge = (right.position.x ?? 0) + getNodeWidth(right) / 2;
        return (leftEdge + rightEdge) / 2;
    };

    parentMap.forEach((parents, childId) => {
        if (parents.length !== 1) return;
        if ((childrenByParent.get(childId) ?? []).length > 0) return;

        const parentId = parents[0];
        const childNode = nodeById.get(childId);
        const parentNode = nodeById.get(parentId);
        if (!childNode || !parentNode) return;

        const childPartnerId = getPartnerId(childId);
        const parentPartnerId = getPartnerId(parentId);
        if (!childPartnerId || !parentPartnerId) return;

        const childPartner = nodeById.get(childPartnerId);
        const parentPartner = nodeById.get(parentPartnerId);
        if (!childPartner || !parentPartner) return;

        const childLevel = levelById.get(childId);
        const parentLevel = levelById.get(parentId);
        if (childLevel === undefined || parentLevel === undefined) return;
        if (childLevel !== parentLevel + 1) return;
        if (parentLevel < 1) return;

        const parentCenter = getPairCenter(parentNode, parentPartner);
        const childCenter = getPairCenter(childNode, childPartner);
        const shift = parentCenter - childCenter;
        if (!Number.isFinite(shift) || Math.abs(shift) < 0.5) return;

        childNode.position.x = (childNode.position.x ?? 0) + shift;
        childPartner.position.x = (childPartner.position.x ?? 0) + shift;
    });
}

export function centerExtendedFamilyAboveMain(nodes: Node[], mainMemberIds: string[]) {
    if (mainMemberIds.length === 0) return;
    const mainNodes = nodes.filter(node => mainMemberIds.includes(node.id));
    if (mainNodes.length === 0) return;
    const mainCenter = mainNodes.map(n => n.position.x ?? 0).reduce((a, b) => a + b, 0) / mainNodes.length;

    const extendedNodes = nodes.filter(node => node.type === 'familyMember' && (node.position.y ?? 0) < 0);
    if (extendedNodes.length === 0) return;
    const left = Math.min(...extendedNodes.map(n => (n.position.x ?? 0) - getNodeWidth(n) / 2));
    const right = Math.max(...extendedNodes.map(n => (n.position.x ?? 0) + getNodeWidth(n) / 2));
    const extendedCenter = (left + right) / 2;
    const shift = mainCenter - extendedCenter;
    extendedNodes.forEach(node => {
        node.position.x = (node.position.x ?? 0) + shift;
    });
}

export function enforceExtendedFamilySides(
    nodes: Node[],
    primarySideParentIds: Set<string>,
    partnerSideParentIds: Set<string>,
    partnerMap: Map<string, string>
) {
    if (primarySideParentIds.size === 0 || partnerSideParentIds.size === 0) return;
    const extendedNodes = nodes.filter(node => node.type === 'familyMember' && (node.position.y ?? 0) < 0);
    if (extendedNodes.length === 0) return;

    const nodeById = new Map(extendedNodes.map(node => [node.id, node]));
    const primaryNodes = extendedNodes.filter(node => primarySideParentIds.has(node.id));
    const partnerNodes = extendedNodes.filter(node => partnerSideParentIds.has(node.id));
    if (primaryNodes.length === 0 || partnerNodes.length === 0) return;

    const expandWithPartners = (seedNodes: Node[]) => {
        const expanded = new Map<string, Node>();
        seedNodes.forEach(node => {
            expanded.set(node.id, node);
            const partnerId = partnerMap.get(node.id);
            if (partnerId) {
                const partnerNode = nodeById.get(partnerId);
                if (partnerNode) expanded.set(partnerNode.id, partnerNode);
            }
        });
        return Array.from(expanded.values());
    };

    const primaryWithPartners = expandWithPartners(primaryNodes);
    const partnerWithPartners = expandWithPartners(partnerNodes);

    const maxPrimaryX = Math.max(...primaryWithPartners.map(n => (n.position.x ?? 0) + getNodeWidth(n) / 2));
    const minPartnerX = Math.min(...partnerWithPartners.map(n => (n.position.x ?? 0) - getNodeWidth(n) / 2));
    const gap = minPartnerX - maxPrimaryX;
    if (gap >= HORIZONTAL_NODE_GAP) return;

    const deficit = HORIZONTAL_NODE_GAP - gap;
    const shiftLeft = deficit / 2;
    const shiftRight = deficit - shiftLeft;

    primaryWithPartners.forEach(node => {
        node.position.x = (node.position.x ?? 0) - shiftLeft;
    });
    partnerWithPartners.forEach(node => {
        node.position.x = (node.position.x ?? 0) + shiftRight;
    });
}
